import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from "react";
import {
  addUserStorage,
  api,
  getToken,
  removeRefreshToken,
  removeToken,
  setRefreshToken,
  setToken,
} from "../api";
import { useNavigate } from "react-router-dom";
import { Loading } from "../../components/Loading";
import { getErrorMessageRecoverPassword } from "../../utils/getErrorMessageRecoverPassword";
import { getErrorMessageVerifyCode } from "utils/getErrorMessageVerifyCode";
import { getErrorMessageResendCode } from "utils/getErrorMessageResendCode";

export interface User {
  id: string;
  nome: string;
  email: string;
  telefone: string;
  empresa: string;
  cidade: string;
  estado: string;
  comissao: string;
  funcao: string;
  nr: string;
  perfil: string;
  permissoes: string;
  coordenador: boolean;
  viceCoordenador: boolean;
}

interface UserProps {
  msg: string;
  content: User;
}

interface AuthData {
  email: string;
  senha: string;
  code?: string;
  microsoft?: boolean;
}

interface AuthContextData {
  user: User;
  signIn(credentials: AuthData): Promise<any>;
  authState: "SignIn" | "SignedIn";
  signOut(): void;
  resendCode(email: string, senha: string): Promise<any>;
  verifyCode(email: string, code: string): Promise<any>;
  isAuthenticated(): any;
  recoverPassword(email: string): Promise<any>;
  me(): Promise<any>;
}

interface AppProps {
  children: React.ReactNode;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export const AuthProvider = ({ children }: AppProps) => {
  const [user, setUser] = useState<User>({} as User);
  const [authState, setAuthState] = useState<"SignIn" | "SignedIn">("SignIn");

  const signIn = useCallback(async ({ email, senha, microsoft, code }: AuthData) => {
    if (microsoft && code) {
      try {
        const response = await api.post("/AutenticacaoMicrosoft", { codigo: code });
        const { token: userToken, refreshToken } = response?.data?.content;

        api.defaults.headers['Authorization'] = `Bearer ${userToken}`;

        setToken(userToken);
        setRefreshToken(refreshToken)
        localStorage.setItem('@user_Outlook', typeof (microsoft))

        return {
          status: "success",
          message: "success",
          data: response.data,
        };

      } catch (error: any) {
        return {
          status: "error",
          message: error?.response?.data?.Error,
        };
      }
    } else {
      try {
        const response = await api.post("/auth/autenticarusuario", { email, senha });

        return {
          status: "success",
          message: "success",
        };
      } catch (error: any) {
        return {
          status: "error",
          message: error?.response?.data?.Error,
        };
      }
    };
  }, []);

  const recoverPassword = useCallback(async (email: string) => {
    try {
      const response = await api.post("/usuario/recuperar-senha", { email });
      return {
        status: "success",
        message: "success",
        data: response.data,
      };
    } catch (error: any) {
      return {
        status: "error",
        message: getErrorMessageRecoverPassword(error)
      };
    }
  }, [])

  const isAuthenticated = useCallback(() => {
    const user = localStorage.getItem('@logged_user');
    if (!user) {
      return {}
    }
    return JSON.parse(user);
  }, []); //validar

  const signOut = useCallback(() => {
    setAuthState("SignIn");
    removeToken();
    removeRefreshToken();

    setUser({} as User);
    window.location.href = '/'
  }, []);

  const me = useCallback(async () => {
    const token = getToken();

    if (token && authState !== "SignedIn") {
      try {
        const response = await api.get<UserProps>("/auth/me");

        if (response.status === 200) {
          const reqUser = JSON.stringify(response?.data?.content);

          addUserStorage(reqUser);
          setUser(response?.data?.content);
          setAuthState("SignedIn");

          return {
            status: "success",
            message: "Success on /me",
            data: response?.data?.content,
          };
        }
      } catch (error: any) {
        setAuthState("SignIn");

        return { status: "error", message: error?.response?.data?.Error };
      }
    }
  }, [authState]);

  const verifyCode = useCallback(async (email: string, codigo: string) => {
    try {
      const response = await api.post("/auth/login", { email, codigo });

      const { token: userToken, refreshToken } = response?.data?.content;

      api.defaults.headers['Authorization'] = `Bearer ${userToken}`;

      setToken(userToken);
      setRefreshToken(refreshToken)

      return {
        status: "success",
        message: "success",
        data: response.data,
      };
    } catch (error: any) {
      return {
        status: "error",
        message: getErrorMessageVerifyCode(error)
      };
    }
  }, [])

  const resendCode = useCallback(async (email: string, senha: string) => {
    try {
      const response = await api.post("/auth/autenticarusuario", { email, senha });
      return {
        status: "success",
        message: "success",
        data: response.data,
      };
    } catch (error: any) {
      return {
        status: "error",
        message: getErrorMessageResendCode(error)
      };
    }
  }, [])

  const values = useMemo(
    () => ({
      user,
      verifyCode,
      resendCode,
      signIn,
      signOut,
      isAuthenticated,
      me,
      recoverPassword,
      authState,
    }),
    [user, verifyCode, resendCode, signIn, signOut, isAuthenticated, me, recoverPassword, authState]
  );

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}
